import { CreditCard, Calendar, Zap, HeartHandshake, ArrowUpRight } from "lucide-react";
import useReveal from "../hooks/useReveal";
import { BRAND } from "../lib/brand";
import { openExternal } from "../lib/openExternal";

const benefits = [
    {
        icon: CreditCard,
        title: "Sin tarjeta de crédito",
        body: "Accede a financiamiento sin necesidad de tarjeta. Aprobación basada en tu perfil.",
    },
    {
        icon: Zap,
        title: "Proceso rápido y sencillo",
        body: "Solicitud 100% en línea. Respuesta ágil para que tu procedimiento no espere.",
    },
    {
        icon: HeartHandshake,
        title: "Atención cuando la necesitas",
        body: "Financia tu cirugía sin posponer tu salud. La medicina avanza, tú también.",
    },
];

const Financiamiento = () => {
    const ref = useReveal();
    const ref2 = useReveal();
    const ref3 = useReveal();

    const handleApply = (e) => {
        e.preventDefault();
        openExternal(BRAND.mendPayUrl, "mendmx.typeform.com");
    };

    return (
        <section
            id="financiamiento"
            data-testid="financiamiento-section"
            className="relative py-24 lg:py-28 bg-cream-warm overflow-hidden"
        >
            <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-gold/[0.06] blur-3xl pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
                    {/* Image */}
                    <div ref={ref3} className="reveal lg:col-span-4 order-2 lg:order-1">
                        <div className="relative crest-frame">
                            <div className="absolute -top-3 -left-3 right-3 bottom-3 border border-gold/30 pointer-events-none" />
                            <div className="relative bg-navy overflow-hidden">
                                <img
                                    src="/financiamiento.png"
                                    alt="Financiamiento médico accesible"
                                    className="block w-full h-auto"
                                    loading="lazy"
                                />
                                <div className="absolute inset-x-0 bottom-0 h-24 pointer-events-none bg-gradient-to-t from-navy/85 to-transparent" />
                                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between bg-navy-deep/85 backdrop-blur-sm border border-gold/30 px-3 py-2.5">
                                    <span className="font-display text-cream text-sm leading-tight">
                                        Tu salud, hoy
                                    </span>
                                    <span className="text-[9px] tracking-[0.28em] uppercase text-gold">
                                        Mend Pay
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* QR Mend Pay */}
                        <a
                            data-testid="financiamiento-qr"
                            href={BRAND.mendPayUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={handleApply}
                            className="mt-6 flex items-center gap-5 bg-white border border-navy/10 p-4 hover:border-gold transition-colors group"
                        >
                            <div className="shrink-0 bg-cream p-2 border border-gold/30">
                                <img
                                    src="/mend-qr.png"
                                    alt="QR · Solicita financiamiento Mend Pay"
                                    className="block w-24 h-24 object-contain"
                                />
                            </div>
                            <div>
                                <p className="text-[10px] tracking-[0.28em] uppercase text-gold/90">
                                    Escanea el QR
                                </p>
                                <p className="mt-1.5 font-display text-navy text-lg leading-tight">
                                    Solicita tu financiamiento
                                </p>
                                <p className="mt-1 text-[12px] text-ink/65 font-light leading-snug">
                                    Acceso directo a tu solicitud en Mend Pay
                                </p>
                            </div>
                        </a>
                    </div>

                    {/* Copy */}
                    <div ref={ref} className="reveal lg:col-span-5 order-1 lg:order-2">
                        <div className="flex items-center gap-4 mb-6">
                            <span className="block w-16 h-px bg-gold" />
                            <span className="eyebrow">Financiamiento</span>
                        </div>

                        <h2
                            data-testid="financiamiento-heading"
                            className="font-display text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.05] text-navy"
                        >
                            Paga tu procedimiento{" "}
                            <span className="italic text-navy/85">
                                a meses sin tarjeta de crédito.
                            </span>
                        </h2>

                        <p className="mt-7 text-ink/75 text-[15px] sm:text-base leading-relaxed font-light">
                            En alianza con{" "}
                            <span className="font-medium text-navy">Mend Pay</span>, mis
                            pacientes acceden a financiamiento médico flexible para que el costo
                            no sea una barrera para su salud. Solicita tu crédito en minutos.
                        </p>

                        <div className="mt-7 inline-flex flex-wrap items-center gap-3 px-5 py-3 border border-navy/15 bg-white/60">
                            <Calendar
                                size={18}
                                className="text-gold shrink-0"
                                strokeWidth={1.5}
                            />
                            <span className="text-[13px] sm:text-sm font-medium text-navy">
                                Planes de
                            </span>
                            <div className="flex items-center gap-2">
                                {["6", "12", "18", "24"].map((m) => (
                                    <span
                                        key={m}
                                        className="font-display text-navy text-base sm:text-lg leading-none border border-gold/40 px-2.5 py-1"
                                    >
                                        {m}
                                    </span>
                                ))}
                            </div>
                            <span className="text-[13px] sm:text-sm font-medium text-navy">
                                meses
                            </span>
                        </div>

                        <div className="mt-9 flex flex-col gap-3">
                            <a
                                data-testid="financiamiento-cta"
                                href={BRAND.mendPayUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={handleApply}
                                className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-navy text-gold text-xs sm:text-sm tracking-[0.22em] uppercase font-semibold hover:bg-navy-deep transition-colors duration-300 w-full sm:w-auto sm:self-start"
                            >
                                Solicita tu financiamiento
                                <ArrowUpRight
                                    size={16}
                                    className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                                />
                            </a>
                            <span className="text-[11px] tracking-[0.22em] uppercase text-ink/55">
                                Powered by Mend Pay · Operación 100% segura
                            </span>
                        </div>
                    </div>

                    {/* Benefits */}
                    <div ref={ref2} className="reveal lg:col-span-3 order-3">
                        <ul className="space-y-3">
                            {benefits.map(({ icon: Icon, title, body }, i) => (
                                <li
                                    key={title}
                                    data-testid={`financiamiento-benefit-${i}`}
                                    className="border border-navy/10 bg-white/80 p-5 group hover:border-gold/50 transition-colors"
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="shrink-0 w-10 h-10 border border-gold/50 flex items-center justify-center text-gold group-hover:bg-gold group-hover:text-navy transition-all duration-300">
                                            <Icon size={18} strokeWidth={1.5} />
                                        </div>
                                        <div className="pt-0.5">
                                            <p className="font-display text-navy text-[1.05rem] leading-tight">
                                                {title}
                                            </p>
                                            <p className="text-[12.5px] text-ink/65 mt-1.5 leading-relaxed font-light">
                                                {body}
                                            </p>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Financiamiento;
